"use client";
import React, { Dispatch, SetStateAction } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import ClientLink from "../ui/client-link";
import { useParams, useSearchParams } from "next/navigation";
import { cn } from "@/lib/utils";
import { CategoryModel } from "@/types/category";
import { useState } from "react";
import { Input } from "../ui/input";
import { Search } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";

const CategoriesSidebar = ({
  categories,
  setOpen,
}: {
  categories: CategoryModel[];
  setOpen?: Dispatch<SetStateAction<boolean>>;
}) => {
  const { id } = useParams();
  const t = useTranslations();
  const searchParams = useSearchParams();
  const subcategory = searchParams.get("subcategory");

  const [searchQuery, setSearchQuery] = useState("");

  const filteredCategories = categories
    .map((cat) => {
      const matchesCategory = cat.name
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const filteredSubs = cat.subcategories?.filter((sub) =>
        sub.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );

      return {
        ...cat,
        subcategories: matchesCategory ? cat.subcategories : filteredSubs,
        isMatch: matchesCategory || (filteredSubs && filteredSubs.length > 0),
      };
    })
    .filter((cat) => cat.isMatch);

  return (
    <div className="space-y-4">
      <div className="relative group">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
        <Input
          placeholder={t("searchCategories")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 bg-muted/50 border-none focus-visible:ring-1 focus-visible:ring-primary/20 transition-all rounded-xl"
        />
      </div>

      <Accordion
        type="single"
        className="w-full space-y-1 max-h-[450px] hover-only-scroll pr-1"
      >
        {filteredCategories.length > 0 ? (
          filteredCategories.map((cat) => (
            <AccordionItem
              key={cat.id}
              value={`cat-${cat.id}`}
              className="border-none"
            >
              <ClientLink href={`/categories/${cat.id}`} scroll={false}>
                <AccordionTrigger
                  className={cn(
                    "flex items-center justify-between p-2.5 text-sm font-medium transition-all duration-200 w-full rounded-xl hover:text-primary hover:bg-primary/5 [&[data-state=open]>svg]:rotate-180",
                    {
                      "bg-primary/10 font-bold text-primary shadow-sm":
                        cat.id.toString() === id,
                    },
                  )}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative size-8 shrink-0 overflow-hidden rounded-full border border-border bg-white shadow-xs">
                      <Image
                        src={cat.image_url}
                        alt={cat.name}
                        fill
                        className="object-contain p-1"
                      />
                    </div>
                    <span>{cat.name}</span>
                  </div>
                </AccordionTrigger>
              </ClientLink>

              {/* ==== SUBCATEGORIES ==== */}
              {cat.subcategories?.length > 0 && (
                <AccordionContent className="mt-1 px-4 space-y-1 pb-2">
                  {cat.subcategories.map((sub) => (
                    <ClientLink
                      key={sub.id}
                      href={`/categories/${cat.id}?subcategory=${sub.id}`}
                      scroll={false}
                      className={cn(
                        "block text-xs px-3 py-2 rounded-lg hover:bg-primary/5 hover:text-primary transition-all duration-200 border-l-2 border-transparent",
                        {
                          "text-primary font-semibold bg-primary/5 border-primary":
                            sub.id.toString() === subcategory,
                        },
                      )}
                      onClick={setOpen ? () => setOpen(false) : undefined}
                    >
                      {sub.name}
                    </ClientLink>
                  ))}
                </AccordionContent>
              )}
            </AccordionItem>
          ))
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4 italic">
            {t("noCategoriesFound")}
          </p>
        )}
      </Accordion>
    </div>
  );
};

export default CategoriesSidebar;
